import { useEffect, useState } from "react";
import { Eye, Users, CalendarDays, Infinity as InfinityIcon } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const L = {
  it: {
    today: "Oggi", d7: "Ultimi 7 giorni", d30: "Ultimi 30 giorni", all: "Sempre",
    views: "visualizzazioni", visitors: "visitatori unici", top: "Pagine più viste",
    daily: "Ultimi 14 giorni", noData: "Nessuna visita registrata per ora."
  },
  en: {
    today: "Today", d7: "Last 7 days", d30: "Last 30 days", all: "All time",
    views: "page views", visitors: "unique visitors", top: "Most viewed pages",
    daily: "Last 14 days", noData: "No visits recorded yet."
  }
};

export default function StatsPanel() {
  const { lang } = useLang();
  const a = L[lang];
  const [stats, setStats] = useState(undefined);

  useEffect(() => {
    api
      .get("/admin/stats?days=30")
      .then(({ data }) => setStats(data))
      .catch(() => setStats(null));
  }, []);

  if (stats === undefined) {
    return <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">…</p>;
  }
  if (!stats || !stats.daily) {
    return <p className="border border-gold/15 py-16 text-center text-sm text-ivory/50" data-testid="stats-empty">{a.noData}</p>;
  }

  const daily = stats.daily.slice(-14);
  const max = Math.max(1, ...daily.map((d) => d.views));
  const cards = [
    { id: "today", label: a.today, icon: CalendarDays, data: stats.today },
    { id: "d7", label: a.d7, icon: Eye, data: stats.last7 },
    { id: "d30", label: a.d30, icon: Eye, data: stats.last30 },
    { id: "all", label: a.all, icon: InfinityIcon, data: stats.allTime },
  ];
  const maxTop = Math.max(1, ...(stats.top || []).map((p) => p.views));

  return (
    <div data-testid="stats-panel">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.id} data-testid={`stat-card-${c.id}`} className="border border-white/10 bg-white/[0.02] p-6">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ivory/50">{c.label}</p>
              <c.icon className="h-4 w-4 text-gold" />
            </div>
            <p className="mt-4 font-serif text-4xl text-ivory" data-testid={`stat-views-${c.id}`}>{c.data.views}</p>
            <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-gold-bright/80">
              <Users className="h-3 w-3" /> {c.data.visitors} {a.visitors}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-10 border border-white/10 bg-white/[0.02] p-6 lg:p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.daily}</p>
        <div className="mt-8 flex h-44 items-end gap-1.5 sm:gap-2" data-testid="stats-chart">
          {daily.map((d) => (
            <div key={d.date} className="group flex h-full flex-1 flex-col items-center justify-end gap-2">
              <div className="relative w-full" style={{ height: `${Math.max(3, (d.views / max) * 100)}%` }}>
                <div className="h-full w-full bg-gold/25 transition-colors group-hover:bg-gold/70" />
                <span className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap border border-gold/30 bg-navy-light px-2 py-1 font-mono text-[10px] text-gold-bright opacity-0 transition-opacity group-hover:opacity-100">
                  {d.views} · {d.visitors} {a.visitors}
                </span>
              </div>
              <span className="font-mono text-[9px] text-ivory/35">{d.date.slice(8)}/{d.date.slice(5, 7)}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.15em] text-ivory/40">
          {a.views} · {a.visitors}
        </p>
      </div>

      <div className="mt-10 border border-white/10 bg-white/[0.02] p-6 lg:p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{a.top}</p>
        <div className="mt-6 space-y-3" data-testid="stats-top-pages">
          {(stats.top || []).map((p) => (
            <div key={p.path} className="flex items-center gap-4">
              <span className="w-56 shrink-0 truncate font-mono text-xs text-ivory/70">{p.path}</span>
              <div className="h-2 flex-1 bg-white/5">
                <div className="h-full bg-gold/60" style={{ width: `${(p.views / maxTop) * 100}%` }} />
              </div>
              <span className="w-12 shrink-0 text-right font-mono text-xs text-gold-bright">{p.views}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
